import PropTypes from 'prop-types';
import { forwardRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Card from "../../common/components/Card";
import DefaultButton from '../../common/components/DefaultButton';
import { Col, Row } from "../../common/components/Grid";
import FormEditor from "../../common/components/GridEditor";
import ReactForm from '../../common/components/ReactForm';
import TextInput from "../../common/components/TextInput";
import useClientConfig from "../../common/hooks/clientConfigHook";
import useCredentials from "../../common/hooks/credentialsHook";
import useNetwork from "../../common/hooks/networkHook";
import { csvEncode } from '../../common/utils/csvUtils';
import { deepEquals } from "../../common/utils/objectUtilities";
import { canCreateProblem, canRemoveProblem, canUpdateProblem, createProblem, removeProblem, updateProblem, useProblems } from "../../reducers/problemSlice";
import { DefaultJobAssistance } from "../assistance/JobAssistance";
import PriorityDropdown from "../PriorityDropdown";
import GroupedSettings from "./GroupedSettings";
import CustomForm from '../../common/components/CustomForm';

const defaultSort = ['problem'];

const newProblemTemplate = { customfields: [], assistance: [], simple: true};

const ProblemDetails = forwardRef(({ value: problem, grouped, groupCtrl, disabled = false, onChange }, formRef) => {
    const clientConfig = useClientConfig();
    const { t } = useTranslation();
    return <>
        <ReactForm ref={formRef}>
            <Row>
                <Col md={8}>
                    <TextInput placeholder={t('problem_one')} readonly={problem.internalid} value={problem.problem} onChange={value => onChange('problem')(value)} disabled={disabled} required></TextInput>
                </Col>
                {/* <Col md={1}>
                    <Checkbox checked={problem.simple} onChange={handleUpdate('simple')} placeholder={t('simple')} disabled={disabled}></Checkbox>
                </Col> */}
            </Row>
            { clientConfig.problemCustomFields && <CustomForm def={clientConfig.problemCustomFields} data={problem.customfields} onChange={value => onChange('customfields')(value)} required></CustomForm> }
            {groupCtrl}
        </ReactForm>
        <Row>
            <Col>
                <Card title={t('jobAssistance')}>
                    <DefaultJobAssistance site={problem.site} level={problem.level} type={grouped ? undefined : problem.type} value={problem.assistance} disabled={disabled} onAdd={value => onChange('assistance')([...problem.assistance, ...value])} onRemove={value => onChange('assistance')(problem.assistance.filter(a => !deepEquals(a, value)))}></DefaultJobAssistance>
                </Card>
            </Col>
        </Row>
        <Row>
            <Col>
                <Card title={t('customfields')}>
                    <FormEditor def={problem.formdef} disabled={disabled} onChange={value => onChange('formdef')(value)}></FormEditor>
                </Card>
            </Col>
        </Row>
    </>
});
ProblemDetails.propTypes = {
    value: PropTypes.object,
    groupCtrl: PropTypes.any,
    grouped: PropTypes.bool,
    disabled: PropTypes.bool,
    onChange: PropTypes.func
}

const Problems = ({ uuid, label, onClose }) => {
    const { t } = useTranslation();
    const credentials = useCredentials();
    const network = useNetwork();
    const clientConfig = useClientConfig();
    const problems = useProblems();
    const sortFields = useMemo(() => ({
        problem: t('problem_one')
    }), [t]);

    const groupingCtrls = useMemo(() => {
        const ctrls = [{ type: 'type', label: t('type', { count: 1 }), title: t('type', { count: 1 }), field: ['site','level','type'], sortfield: 'type', strict: false, wildcard: t('anyWildcard') },
                       { type: 'priority', label: t('priorityDefault'), title: t('priorityDefault'), field: 'priority', sortfield: 'priority', required: true, strict: false }]
        if (clientConfig.enableLevels) {
            ctrls.unshift({ type: 'level', label: t('level', { count: 1 }), title: t('level', { count: 1 }), field: ['site','level','type'], sortfield: 'level', strict: false, wildcard: t('anyWildcard') });
        }
        if (clientConfig.enableSites) {
            ctrls.unshift({ type: 'site', label: t('site', { count: 1 }), title: t('site', { count: 1 }), field: ['site','level','type'], sortfield: 'site', strict: false, wildcard: t('anyWildcard') });
        }
        return ctrls;
    }, [clientConfig.enableLevels, clientConfig.enableSites, t]);

    const onAdd = useCallback(problem => {
        delete problem.internalid;
        return createProblem(network, problem)
    }, [network]);

    const onUpdate = useCallback(delta => {
        return updateProblem(network, delta);
    }, [network]);

    const onRemove = useCallback(problem => {
        return removeProblem(network, problem)
    }, [network]);

    const canAdd = entry => {
        return canCreateProblem(credentials, entry)
    };

    const download = () => {
        let header = '';
        if (clientConfig.enableSites) {
            header += csvEncode(t('site', { count: 1 })) + ',';
        }
        if (clientConfig.enableLevels) {
            header += csvEncode(t('level', { count: 1 })) + ',';
        }
        const csv = header + csvEncode(t('type', { count: 1 })) + ',' + csvEncode(t('problem', { count: 1 })) + '\n' +
            problems.map(p => {
                let result = '';
                if (clientConfig.enableSites) {
                    result += csvEncode(p.site ?? t('anyWildcard')) + ','
                }
                if (clientConfig.enableLevels) {
                    result += csvEncode(p.level ?? t('anyWildcard')) + ','
                }
                return result + `${csvEncode(p.type)},${csvEncode(p.problem)}`;
            }).join('\n');
        network.downloadBlob(new Blob([csv], { type: 'text/csv' }), t('problem', { count: problems.length }) + '.csv');
    }    

    return <GroupedSettings Details={ProblemDetails} data={problems} title={label} uuid={uuid}
            defaultEntry={newProblemTemplate} defaultSort={defaultSort} sortFields={sortFields}
            onClose={onClose} makeLabel={e => e.problem}
            initialGroupingField="problem"
            groupingExclusions={['site','level','type','internalid','editid']}
            canAdd={canAdd} canUpdate={entry => canUpdateProblem(credentials, entry)} canRemove={entry => canRemoveProblem(credentials, entry)}
            onAdd={onAdd} onUpdate={onUpdate} onRemove={onRemove}
            headerControls={<DefaultButton kind="download" onClick={download}></DefaultButton>}
            groupingCtrls={groupingCtrls}></GroupedSettings>
}
Problems.propTypes = {
    /**
     * ID to identify the settings status entry
     */
    uuid: PropTypes.string.isRequired,
    /**
     * Label for the screen
     */
    label: PropTypes.string.isRequired,
    /**
     * Function to call when the screen is closed
     */
    onClose: PropTypes.func
}
export default Problems;